import React from 'react';
import { Link } from 'react-router-dom';

import { RELATIONS } from '../visit/RegisterVisitForm';
import { ROUTES } from '../../constants/routes';
import { formatDateIST } from '../../utils/formatIST';

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

function relationLabel(relation) {
  if (!relation) return null;
  return RELATIONS.find((r) => r.value === relation)?.label || relation;
}

/**
 * DirectoryResultRow (EPIC PATIENT-CARD, F4) — one row of the §1.3
 * `patients[]` array. Navigates to the `ROUTES.PATIENT_CARD` route, built
 * from the shared constant rather than a second copy of the path literal
 * (F4 wrote the path out because F9 had not registered the constant yet;
 * F9 has, so the row now reads it).
 *
 * UHID is the REAL `uhid` field, rendered "—" when null — NEVER a sliced
 * `patient_id` (the legacy `patient_id`/`patient_number` UHID fallback is a
 * server-side match rule in the directory lane, §1.3, not something this row
 * re-derives).
 *
 * Shows "Registered <date>" from `created_at`, never a "Last visit" column.
 * `directory.html`'s mockup prints "Last visit N days ago" — that is a
 * deliberate, owner-approved deviation (D17 / `bk-806`): patient rows carry
 * no last-visit field, and computing one would cost a query PER ROW, which
 * breaks the search. Do not "fix" this row to match the mockup.
 */
export default function DirectoryResultRow({ patient }) {
  const name =
    patient.full_name ||
    `${patient.first_name || ''} ${patient.last_name || ''}`.trim() ||
    'Unnamed patient';
  const rel = relationLabel(patient.relation);
  const registered = formatDateIST(patient.created_at);

  return (
    <Link
      to={ROUTES.PATIENT_CARD.replace(':patientId', encodeURIComponent(patient.patient_id))}
      className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 no-underline"
      data-testid={`directory-row-${patient.patient_id}`}
    >
      <span
        className="w-9 h-9 shrink-0 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200 font-bold text-[12px] flex items-center justify-center"
        aria-hidden="true"
      >
        {initials(name)}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className="block font-semibold text-slate-900 dark:text-white text-[13.5px] truncate"
          data-testid={`directory-row-name-${patient.patient_id}`}
        >
          {name}
        </span>
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">
          <span
            className="px-1.5 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 font-semibold text-slate-600 dark:text-slate-300"
            data-testid={`directory-row-uhid-${patient.patient_id}`}
          >
            {patient.uhid || '—'}
          </span>
          {patient.age && <span>{`Age ${patient.age}`}</span>}
          {patient.gender && <span>{patient.gender}</span>}
          {patient.mobile && <span>{patient.mobile}</span>}
          {rel && <span>{rel}</span>}
          <span data-testid={`directory-row-registered-${patient.patient_id}`}>
            {registered ? `Registered ${registered}` : 'Registration date unknown'}
          </span>
        </span>
      </span>
      <span className="shrink-0 text-[12px] font-semibold text-teal-700 dark:text-teal-300">
        Open card →
      </span>
    </Link>
  );
}
